/**
 * ══════════════════════════════════════════════════════════════
 * InsightCard — Componente do Design System para Insights Analíticos
 * ══════════════════════════════════════════════════════════════
 *
 * Exibe insight derivado dos dados com:
 * - Indicador visual de severidade
 * - Recomendação de ação
 * - Drill-down para investigação
 *
 * Uso: import { InsightCard } from '@/components/system';
 * ══════════════════════════════════════════════════════════════
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Info, ShieldAlert, ArrowRight, Lightbulb } from 'lucide-react';
import type { AnalyticalInsight, InsightSeverity } from '@/types/insights';

// ─── Severity Config ────────────────────────────────────────

const SEVERITY_CONFIG: Record<InsightSeverity, {
  icon: React.ElementType;
  containerClass: string;
  iconClass: string;
  badgeClass: string;
  label: string;
}> = {
  critical: {
    icon: ShieldAlert,
    containerClass: 'border-destructive/30 bg-destructive/5',
    iconClass: 'text-destructive',
    badgeClass: 'bg-destructive/10 text-destructive',
    label: 'Crítico',
  },
  warning: {
    icon: AlertTriangle,
    containerClass: 'border-orange-500/30 bg-orange-500/5',
    iconClass: 'text-orange-500',
    badgeClass: 'bg-orange-500/10 text-orange-600',
    label: 'Atenção',
  },
  info: {
    icon: Info,
    containerClass: 'border-primary/30 bg-primary/5',
    iconClass: 'text-primary',
    badgeClass: 'bg-primary/10 text-primary',
    label: 'Informativo',
  },
};

// ─── Props ──────────────────────────────────────────────────

export interface InsightCardProps {
  insight: AnalyticalInsight;
  /** Compact mode for sidebar/list usage */
  compact?: boolean;
  /** Custom click handler (overrides drillDownRoute) */
  onClick?: (insight: AnalyticalInsight) => void;
  className?: string;
}

// ─── Component ──────────────────────────────────────────────

export function InsightCard({ insight, compact = false, onClick, className = '' }: InsightCardProps) {
  const navigate = useNavigate();
  const config = SEVERITY_CONFIG[insight.severity];
  const Icon = config.icon;

  const handleClick = () => {
    if (onClick) {
      onClick(insight);
    } else if (insight.drillDownRoute) {
      navigate(insight.drillDownRoute);
    }
  };

  const isClickable = !!(onClick || insight.drillDownRoute);

  if (compact) {
    return (
      <button
        onClick={handleClick}
        disabled={!isClickable}
        className={`w-full text-left flex items-start gap-2.5 p-2.5 rounded-lg border transition-colors
          ${config.containerClass}
          ${isClickable ? 'cursor-pointer hover:bg-muted/40' : 'cursor-default'}
          ${className}`}
      >
        <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${config.iconClass}`} />
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium leading-tight truncate">{insight.title}</p>
          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{insight.description}</p>
        </div>
        {isClickable && <ArrowRight className="h-3 w-3 mt-1 shrink-0 text-muted-foreground/50" />}
      </button>
    );
  }

  return (
    <div
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onClick={isClickable ? handleClick : undefined}
      onKeyDown={isClickable ? (e) => { if (e.key === 'Enter') handleClick(); } : undefined}
      className={`rounded-lg border p-4 transition-all
        ${config.containerClass}
        ${isClickable ? 'cursor-pointer hover:shadow-md hover:-translate-y-0.5' : ''}
        ${className}`}
    >
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-lg ${config.badgeClass}`}>
          <Icon className={`h-4 w-4 ${config.iconClass}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-xs font-semibold uppercase tracking-wider ${config.iconClass}`}>
              {config.label}
            </span>
            {insight.relatedEntity && (
              <span className="text-xs text-muted-foreground truncate">
                • {insight.relatedEntity.name}
              </span>
            )}
          </div>
          <h4 className="text-sm font-semibold leading-tight">{insight.title}</h4>
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{insight.description}</p>
        </div>
        {isClickable && (
          <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground/40 mt-1" />
        )}
      </div>

      {/* Recommended Action */}
      {insight.recommendedAction && (
        <div className="mt-3 flex items-start gap-2 p-2.5 rounded-md bg-muted/30">
          <Lightbulb className="h-3.5 w-3.5 mt-0.5 shrink-0 text-muted-foreground" />
          <p className="text-xs text-muted-foreground leading-relaxed">
            <span className="font-medium">Ação recomendada:</span> {insight.recommendedAction}
          </p>
        </div>
      )}
    </div>
  );
}

// ─── List Component ─────────────────────────────────────────

export interface InsightListProps {
  insights: AnalyticalInsight[];
  /** Max insights to show (default: 5) */
  maxItems?: number;
  /** Compact mode */
  compact?: boolean;
  /** Custom click handler */
  onInsightClick?: (insight: AnalyticalInsight) => void;
  className?: string;
}

export function InsightList({
  insights,
  maxItems = 5,
  compact = false,
  onInsightClick,
  className = '',
}: InsightListProps) {
  const displayed = insights.slice(0, maxItems);

  if (displayed.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
        <Info className="h-8 w-8 mb-2 opacity-40" />
        <p className="text-sm">Nenhum insight identificado</p>
        <p className="text-xs mt-1">Os dados atuais não acionaram nenhuma regra analítica</p>
      </div>
    );
  }

  return (
    <div className={`space-y-2 ${className}`}>
      {displayed.map((insight) => (
        <InsightCard
          key={insight.id}
          insight={insight}
          compact={compact}
          onClick={onInsightClick}
        />
      ))}
      {insights.length > maxItems && (
        <p className="text-xs text-muted-foreground text-center pt-1">
          +{insights.length - maxItems} insights adicionais
        </p>
      )}
    </div>
  );
}

export default InsightCard;
